import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the police-clearance-certificate guide. */
const TITLE = "Lý lịch tư pháp Thái Lan dùng ở nước ngoài — hướng dẫn đầy đủ";
const DESC =
  "Cách xin lý lịch tư pháp Thái Lan, giấy tờ cần chuẩn bị, các lớp chứng nhận sau đó tại Cục Lãnh sự và đại sứ quán, cùng những lý do thường bị trả lại.";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Ai có thể xin lý lịch tư pháp Thái Lan?",
    answer:
      "Người Thái và người nước ngoài từng lưu trú tại Thái Lan đều có thể xin. Người đang ở nước ngoài thường nộp qua người được uỷ quyền hoặc qua đại sứ quán Thái Lan.",
  },
  {
    question: "Cần chuẩn bị những giấy tờ gì?",
    answer:
      "Thông thường gồm hộ chiếu hoặc chứng minh nhân dân, ảnh chụp theo quy định, bản lấy dấu vân tay theo mẫu, giấy tờ thể hiện mục đích sử dụng, và văn bản uỷ quyền nếu nhờ người khác nộp thay.",
  },
  {
    question: "Mất bao lâu?",
    answer:
      "Thời gian phụ thuộc vào cơ quan cấp và cách nộp. Hãy tính thêm thời gian cho các bước chứng nhận sau đó, vì đây là phần thường bị quên khi lập kế hoạch.",
  },
  {
    question: "Giấy tờ có thời hạn không?",
    answer:
      "Bản thân giấy không ghi hạn dùng, nhưng nhiều cơ quan tiếp nhận chỉ nhận bản cấp trong vòng 3–6 tháng. Hãy xin theo lịch nộp hồ sơ.",
  },
  {
    question: "Sau khi có giấy thì làm gì tiếp?",
    answer:
      "Nếu dùng ở nước ngoài, thường phải chứng nhận tại Cục Lãnh sự Bộ Ngoại giao, dịch sang ngôn ngữ yêu cầu, và tuỳ nước còn phải hợp pháp hoá tại đại sứ quán nước tiếp nhận.",
  },
  {
    question: "Dịch trước hay chứng nhận trước?",
    answer:
      "Chứng nhận trước rồi mới dịch, để bản dịch bao gồm cả con dấu và dòng chú thích được thêm vào ở các bước sau.",
  },
  {
    question: "Vì sao hồ sơ bị trả lại?",
    answer:
      "Phổ biến nhất là sai thứ tự các bước, tên viết không khớp hộ chiếu, bản lấy dấu vân tay không đạt chuẩn, và giấy quá hạn theo yêu cầu của cơ quan tiếp nhận.",
  },
];

export const Route = createFileRoute("/vi/blog/police-clearance-certificate-guide")({
  component: ViPccGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "lý lịch tư pháp Thái Lan, police clearance Thái Lan, chứng nhận lý lịch tư pháp, hợp pháp hoá giấy tờ Thái Lan",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Hộ chiếu hoặc chứng minh nhân dân", note: "Cách viết tên phải khớp mọi giấy tờ trong bộ hồ sơ" },
  { d: "Ảnh chụp theo quy định", note: "Chụp mới, đúng kích cỡ và phông nền mà cơ quan yêu cầu" },
  { d: "Bản lấy dấu vân tay theo mẫu", note: "Lấy tại cơ quan công an hoặc nơi được chấp nhận; bản mờ sẽ bị từ chối" },
  { d: "Giấy tờ thể hiện mục đích sử dụng", note: "Ví dụ thư mời làm việc, hồ sơ visa, thông báo nhập học" },
  { d: "Văn bản uỷ quyền (nếu nhờ người nộp thay)", note: "Nêu rõ phạm vi: xin, nhận, chứng nhận và nộp hồ sơ" },
  { d: "Giấy tờ lưu trú tại Thái Lan (với người nước ngoài)", note: "Ví dụ trang visa, giấy tờ cư trú hoặc giấy phép làm việc" },
];

const ORDER: string[] = [
  "Lấy danh mục chính thức của cơ quan tiếp nhận ở nước ngoài",
  "Chuẩn bị giấy tờ và bản lấy dấu vân tay đúng chuẩn",
  "Nộp đơn xin lý lịch tư pháp và nhận bản gốc",
  "Chứng nhận tại Cục Lãnh sự Bộ Ngoại giao khi được yêu cầu",
  "Dịch toàn văn kèm con dấu, tem và chú thích",
  "Hợp pháp hoá tại đại sứ quán nước tiếp nhận nếu cần, rồi rà soát lần cuối",
];

const MISTAKES: string[] = [
  "Dịch trước khi hoàn tất các lớp chứng nhận",
  "Bản lấy dấu vân tay mờ hoặc sai mẫu",
  "Tên và ngày sinh không khớp hộ chiếu",
  "Xin quá sớm nên hết thời hạn khi nộp hồ sơ",
  "Uỷ quyền có phạm vi hẹp nên không nhận hoặc nộp thay được",
  "Thiếu bản dịch của con dấu và dòng chú thích",
];

function ViPccGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="vi" />
      <p className="answer-summary speakable-summary">
        Lý lịch tư pháp Thái Lan dùng ở nước ngoài hiếm khi được nhận nếu chỉ có bản gốc. Trình tự thực tế là “xin bản
        gốc → chứng nhận tại Cục Lãnh sự → dịch → hợp pháp hoá tại đại sứ quán nếu cần”. Hãy lấy danh mục chính thức của
        cơ quan tiếp nhận trước, rồi xin giấy theo lịch nộp để không hết thời hạn.
      </p>

      <h2>1. Giấy tờ cần chuẩn bị</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Thứ tự các bước</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Lý do thường bị trả lại</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>{" "}
        ·{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          Cảnh sát Hoàng gia Thái Lan
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/pcc" className="text-primary underline">
            Dịch vụ lý lịch tư pháp Thái Lan
          </Link>
        </li>
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
        <li>
          <Link to="/vi/visa" className="text-primary underline">
            Dịch vụ hỗ trợ hồ sơ visa
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Quy định của từng cơ quan có thể thay đổi, nên hãy xác nhận yêu cầu mới nhất
        trước khi bắt đầu. Nếu bạn không muốn tự theo từng bước, đội ngũ hơn 15 năm kinh nghiệm của chúng tôi có thể tư
        vấn và thực hiện toàn bộ — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
